import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Download, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useGlobalAdminStatus } from '@/hooks/useGlobalAdminStatus';
import { authenticatedBitswanBackendInstance } from '@/lib/api-client';

type PayoutRow = {
  id: string; advisor_name: string; amount: string; currency: string; invoice_filename: string;
  note: string; status: 'requested' | 'paid' | 'rejected'; status_display: string;
  requested_at: string; requested_by_email: string; resolution_note: string;
};

const STATUS_TONE: Record<string, string> = {
  pending: 'bg-amber-500/15 text-amber-700',
  overdue: 'bg-red-500/15 text-red-700',
  paid: 'bg-emerald-500/15 text-emerald-700',
  cancelled: 'bg-muted text-muted-foreground',
  requested: 'bg-amber-500/15 text-amber-700',
  rejected: 'bg-red-500/15 text-red-700',
};

export default function AdvisorPayouts() {
  const queryClient = useQueryClient();
  const { role } = useGlobalAdminStatus();
  const canAct = role === 'admin';
  const [note, setNote] = React.useState<Record<string, string>>({});

  const payouts = useQuery({
    queryKey: ['global-admin-payouts'],
    queryFn: async () => {
      const api = await authenticatedBitswanBackendInstance();
      return (await api.get('/global-admin/payouts/')).data as PayoutRow[];
    },
  });

  const resolve = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: 'paid' | 'rejected' }) => {
      const api = await authenticatedBitswanBackendInstance();
      return api.post(`/global-admin/payouts/${id}/resolve/`, { status, note: note[id] || '' });
    },
    onSuccess: () => { toast.success('Payout updated'); queryClient.invalidateQueries({ queryKey: ['global-admin-payouts'] }); },
    onError: (err: any) => toast.error(err.response?.data?.error || err.message),
  });

  const download = async (row: PayoutRow) => {
    const api = await authenticatedBitswanBackendInstance();
    const res = await api.get(`/advisor/billing/payouts/${row.id}/invoice/`, { responseType: 'blob' });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a'); a.href = url; a.download = row.invoice_filename; a.click();
    URL.revokeObjectURL(url);
  };

  return (
  <Card data-testid="admin-payouts">
    <CardHeader>
      <CardTitle>Advisor payouts</CardTitle>
      <CardDescription>Advisors invoice BitSwan for what their clients have paid. Download the invoice, pay it, mark it paid.</CardDescription>
    </CardHeader>
    <CardContent>
      {payouts.isLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : (
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-muted-foreground">
            <tr><th className="py-1">Requested</th><th className="py-1">Advisor</th><th className="py-1 text-right">Amount</th><th className="py-1">Invoice</th><th className="py-1 text-right">Status</th><th /></tr>
          </thead>
          <tbody>
            {(payouts.data ?? []).map((row) => (
              <tr key={row.id} className="border-t align-top" data-testid={`admin-payout-${row.id}`}>
                <td className="py-2">{new Date(row.requested_at).toLocaleDateString()}<br /><span className="text-xs text-muted-foreground">{row.requested_by_email}</span></td>
                <td className="py-2">{row.advisor_name}{row.note && <p className="text-xs text-muted-foreground">{row.note}</p>}</td>
                <td className="py-2 text-right font-medium">{row.amount} {row.currency}</td>
                <td className="py-2">
                  <button type="button" className="inline-flex items-center gap-1 text-xs hover:underline" onClick={() => download(row)}>
                    <Download className="h-3 w-3" /> {row.invoice_filename}
                  </button>
                </td>
                <td className="py-2 text-right"><Badge className={STATUS_TONE[row.status] ?? ''} data-testid={`admin-payout-status-${row.id}`}>{row.status_display}</Badge>{row.resolution_note && <p className="mt-1 text-xs text-muted-foreground">{row.resolution_note}</p>}</td>
                <td className="py-2 text-right">
                  {canAct && row.status === 'requested' && (
                    <div className="flex flex-col items-end gap-1">
                      <input className="h-8 w-40 rounded-md border px-2 text-xs" placeholder="Note (optional)" value={note[row.id] ?? ''} onChange={(e) => setNote({ ...note, [row.id]: e.target.value })} />
                      <div className="flex gap-1">
                        <Button size="sm" onClick={() => resolve.mutate({ id: row.id, status: 'paid' })} disabled={resolve.isPending} data-testid={`payout-paid-${row.id}`}><Check className="mr-1 h-3.5 w-3.5" /> Paid</Button>
                        <Button size="sm" variant="outline" onClick={() => resolve.mutate({ id: row.id, status: 'rejected' })} disabled={resolve.isPending} data-testid={`payout-reject-${row.id}`}><X className="h-3.5 w-3.5" /></Button>
                      </div>
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {payouts.data?.length === 0 && <tr><td colSpan={6} className="py-6 text-center text-muted-foreground">No payout requests.</td></tr>}
          </tbody>
        </table>
      )}
    </CardContent>
  </Card>
  );
}
